function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export async function extractError(
  response: Response,
  fallback: string,
): Promise<string> {
  const body = await parseErrorBody(response);
  return extractErrorMessage(body, fallback, response.status);
}

async function parseErrorBody(
  response: Response,
): Promise<Record<string, unknown> | null> {
  try {
    const body = await response.json();
    return isRecord(body) ? body : null;
  } catch {
    return null;
  }
}

function extractErrorMessage(
  body: Record<string, unknown> | null,
  fallback: string,
  statusCode: number,
): string {
  if (!body) {
    return `${fallback} (HTTP ${statusCode})`;
  }

  const error = body.error;
  if (typeof error === 'string') return error;

  if (isRecord(error)) {
    if (typeof error.message === 'string') return error.message;
    if (typeof error.name === 'string') return error.name;
    return JSON.stringify(error);
  }

  if (typeof body.message === 'string') return body.message;
  return fallback;
}

export async function throwActivityReadError(
  response: Response,
  fallback: string,
): Promise<never> {
  let code: string | undefined;
  let message = fallback;
  try {
    const body = await response.json();
    if (typeof body.code === 'string') code = body.code;
    if (typeof body.error === 'string') {
      message = body.error;
    } else if (body.error && typeof body.error.message === 'string') {
      message = body.error.message;
    } else if (typeof body.message === 'string') {
      message = body.message;
    }
  } catch {
    message = `${fallback} (HTTP ${response.status})`;
  }
  const err = new Error(message) as Error & {
    code?: string;
    status?: number;
  };
  err.status = response.status;
  if (code) err.code = code;
  throw err;
}

/** Surfaces the backend's deterministic public message instead of a generic one. */
export async function deletionErrorMessage(
  response: Response,
): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string };
    if (body?.message) return body.message;
  } catch {
    // Falls through to the generic message.
  }
  return 'A exclusão administrativa não pôde ser concluída.';
}
